import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge, Box, DropdownMenuItem, Row, TableListActions, TableListRow, Typography } from 'seagull';
import { Eye } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { getStatusBadgeSkin, invoiceLogicTextsPath } from '~/business-logic/invoiceLogic';
import { getURLWithFullPageInvoice } from '~/components/FullPageInvoice/FullPageInvoice.utils';
import { InvoiceActionsMenuItems } from '~/components/InvoiceActions/InvoiceActionsMenuItems';
import { InvoiceNumberCell } from '~/components/InvoiceRow/InvoiceRowCells';
import { useLocaleKeys } from '~/i18n';
import { invoiceSummaryModel, timeModel } from '~/models';
import styles from './ClientInvoicesListView.module.scss';
import { getAdditionalInfoText, getStatusText } from './ClientInvoicesListView.utils';
export const GetClientInvoicesListViewRowTID = (parentTestId)=>getTestIds(parentTestId);
export function ClientInvoicesListViewRow(param) {
    let { invoice, customerId, showAgreementName, testId, compact = false, disableMarginBottom, hideManagePaymentMethods = false } = param;
    const { ClientInvoicesListViewRowTestIds: TID } = useMemo(()=>GetClientInvoicesListViewRowTID(testId), [
        testId
    ]);
    const { t: invoiceLogicTexts } = useLocaleKeys(invoiceLogicTextsPath);
    const { t: tableRowTexts } = useLocaleKeys('invoice.tableRow');
    const relationshipId = invoiceSummaryModel.relationshipId(invoice);
    const to = getURLWithFullPageInvoice({
        invoiceId: invoice.id,
        relationshipId
    });
    const displayStatus = invoiceSummaryModel.displayStatus(invoice);
    const statusText = getStatusText(invoice, invoiceLogicTexts);
    const additionalInfoText = getAdditionalInfoText(invoice, invoiceLogicTexts);
    const agreementName = invoiceSummaryModel.relationshipTitle(invoice);
    const statusBadgeSkin = getStatusBadgeSkin(displayStatus);
    const statusCellValue = /*#__PURE__*/ _jsx(StatusBadge, {
        skin: statusBadgeSkin,
        testId: TID.Status(),
        children: statusText
    });
    const numberCellValue = /*#__PURE__*/ _jsx(Box, {
        children: compact ? /*#__PURE__*/ _jsxs(Row, {
            gap: 8,
            alignItems: "center",
            children: [
                /*#__PURE__*/ _jsx(InvoiceNumberCell, {
                    invoice: invoice,
                    testId: TID.InvoiceNumber(),
                    accountingIconSize: "xsmall"
                }),
                /*#__PURE__*/ _jsx(StatusBadge, {
                    skin: statusBadgeSkin,
                    testId: TID.StatusMobile(),
                    children: statusText
                })
            ]
        }) : /*#__PURE__*/ _jsx(InvoiceNumberCell, {
            invoice: invoice,
            testId: TID.InvoiceNumber(),
            accountingIconSize: "xsmall"
        })
    });
    const totalCellValue = /*#__PURE__*/ _jsx(Typography, {
        weight: "normal",
        testId: TID.Total(),
        children: invoice.totalAmount
    });
    const items = [
        {
            value: numberCellValue
        },
        showAgreementName && {
            value: /*#__PURE__*/ _jsx(Typography, {
                ellipsis: true,
                skin: "secondary",
                testId: TID.AgreementName(),
                children: agreementName
            })
        },
        {
            value: statusCellValue
        },
        {
            value: /*#__PURE__*/ _jsx(Typography, {
                ellipsis: true,
                skin: "secondary",
                testId: TID.AdditionalInfo(),
                children: additionalInfoText
            })
        },
        {
            value: /*#__PURE__*/ _jsx(Typography, {
                skin: "secondary",
                testId: TID.IssueDate(),
                children: timeModel.formatDateLong(invoice.rawIssuedOn.getTime())
            })
        },
        {
            value: /*#__PURE__*/ _jsx(Typography, {
                skin: "secondary",
                testId: TID.DueDate(),
                children: timeModel.formatDateLong(invoice.rawDueDate.getTime())
            })
        },
        {
            value: totalCellValue
        }
    ];
    return /*#__PURE__*/ _jsx(TableListRow, {
        as: Link,
        to: to,
        size: "large",
        testId: testId,
        className: styles.row,
        disableMarginBottom: disableMarginBottom,
        mobile: {
            skin: 'white'
        },
        items: items.filter((i)=>!!i),
        actionColumn: /*#__PURE__*/ _jsxs(TableListActions, {
            bi: {
                button: {
                    name: 'client_invoices_view'
                },
                menuTrigger: {
                    name: 'client_invoices_actions'
                }
            },
            testId: TID.Menu(),
            mobile: {
                title: invoice.formattedNumber,
                subtitle: agreementName
            },
            children: [
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    testId: TID.View(),
                    bi: {
                        name: 'view_invoice'
                    },
                    iconStart: /*#__PURE__*/ _jsx(Eye, {}),
                    as: Link,
                    to: to,
                    children: tableRowTexts.viewInvoice()
                }),
                /*#__PURE__*/ _jsx(InvoiceActionsMenuItems, {
                    invoice: invoice,
                    customerId: customerId,
                    testId: TID.Menu(),
                    hideManagePaymentMethods: hideManagePaymentMethods
                })
            ]
        })
    }, invoice.id);
}
